// src/components/Shared/MusicControlPanel.jsx
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { karaokeLyrics } from '../../config/karaokeConfig';
import KaraokeDisplay from './KaraokeDisplay';

const colorConfig = {
  purple: {
    hex: '#c026d3',
    gradient: 'from-fuchsia-500 to-purple-600',
    glow: 'rgba(192, 38, 211, 0.45)',
    badgeBg: 'rgba(192, 38, 211, 0.15)',
    badgeBorder: 'rgba(192, 38, 211, 0.3)',
    badgeText: '#f0abfc',
    sliderColor: '#c026d3'
  },
  blue: {
    hex: '#3b82f6',
    gradient: 'from-blue-500 to-indigo-600',
    glow: 'rgba(59, 130, 246, 0.45)',
    badgeBg: 'rgba(59, 130, 246, 0.15)',
    badgeBorder: 'rgba(59, 130, 246, 0.3)',
    badgeText: '#93c5fd',
    sliderColor: '#3b82f6'
  },
  orange: {
    hex: '#f97316',
    gradient: 'from-amber-500 to-orange-600',
    glow: 'rgba(249, 115, 22, 0.45)',
    badgeBg: 'rgba(249, 115, 22, 0.15)',
    badgeBorder: 'rgba(249, 115, 22, 0.3)',
    badgeText: '#fdba74',
    sliderColor: '#f97316'
  },
  red: {
    hex: '#ef4444',
    gradient: 'from-red-500 to-rose-600',
    glow: 'rgba(239, 68, 68, 0.45)',
    badgeBg: 'rgba(239, 68, 68, 0.15)',
    badgeBorder: 'rgba(239, 68, 68, 0.3)',
    badgeText: '#fca5a5',
    sliderColor: '#ef4444'
  },
  white: {
    hex: '#ffffff',
    gradient: 'from-white to-gray-300',
    glow: 'rgba(255, 255, 255, 0.4)',
    badgeBg: 'rgba(255, 255, 255, 0.12)',
    badgeBorder: 'rgba(255, 255, 255, 0.25)',
    badgeText: '#ffffff',
    sliderColor: '#ffffff'
  },
  cyan: {
    hex: '#06b6d4',
    gradient: 'from-cyan-400 to-teal-500',
    glow: 'rgba(6, 182, 212, 0.45)',
    badgeBg: 'rgba(6, 182, 212, 0.15)',
    badgeBorder: 'rgba(6, 182, 212, 0.3)',
    badgeText: '#67e8f9',
    sliderColor: '#06b6d4'
  }
};

const formatTime = (secs = 0) => {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const MusicControlPanel = ({
  isPlaying,
  volume,
  setVolume,
  isMuted,
  setIsMuted,
  currentTime = 0,
  currentTrack = '',
  lyrics: passedLyrics = [],
  analyser,
  beatParams,
  accentColor = 'purple',
  className = '',
  onNextTrack,
  nextTrack
}) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';
  const c = colorConfig[accentColor] || colorConfig.purple;

  const handleSkip = onNextTrack || nextTrack;

  const handleVolumeChange = useCallback(
    (e) => {
      setVolume?.(parseFloat(e.target.value));
    },
    [setVolume]
  );

  const toggleMute = useCallback(() => {
    setIsMuted?.((v) => !v);
  }, [setIsMuted]);

  // Synchronized lyrics calculation
  const lyrics = useMemo(() => {
    if (passedLyrics && passedLyrics.length > 0) return passedLyrics;
    if (!currentTrack) return [];
    return karaokeLyrics[currentTrack] || karaokeLyrics['default'] || [];
  }, [passedLyrics, currentTrack]);

  // Animated visualizer bars using Web Audio API analyser
  const [barHeights, setBarHeights] = useState([8, 12, 16, 22, 18, 14, 20, 10]);

  useEffect(() => {
    if (!isPlaying || isMuted) {
      setBarHeights([4, 4, 4, 4, 4, 4, 4, 4]);
      return;
    }

    let animId;
    const updateBars = () => {
      if (analyser) {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        const step = Math.floor(bufferLength / 8);
        const next = [];
        for (let i = 0; i < 8; i++) {
          const val = dataArray[i * step] || 0;
          const height = Math.max(4, Math.round((val / 255) * 22));
          next.push(height);
        }
        setBarHeights(next);
      } else {
        // Fallback rhythmic wave
        const now = Date.now() / 150;
        const wave = Array.from({ length: 8 }, (_, i) =>
          Math.max(4, Math.round(12 + Math.sin(now + i * 0.8) * 8))
        );
        setBarHeights(wave);
      }
      animId = requestAnimationFrame(updateBars);
    };

    animId = requestAnimationFrame(updateBars);
    return () => cancelAnimationFrame(animId);
  }, [isPlaying, isMuted, analyser]);

  if (!isPlaying) return null;

  const fillPercent = Math.round((volume || 0) * 100);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.98 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className={`w-full flex flex-col gap-2 ${className}`}
      >
        {/* Full-space Bouncing Dynamic Text Display */}
        <KaraokeDisplay
          currentTime={currentTime}
          currentTrack={currentTrack}
          lyrics={lyrics}
          analyser={analyser}
          accentColor={accentColor}
          beatParams={beatParams}
        />

        {/* Cyber Music Player Controls Deck */}
        <div
          className="cyber-music-deck relative rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-2.5 overflow-hidden border backdrop-blur-2xl transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, rgba(14, 14, 20, 0.85) 0%, rgba(8, 8, 12, 0.95) 100%)',
            borderColor: 'rgba(255, 255, 255, 0.09)',
            boxShadow: `0 8px 28px -6px rgba(0, 0, 0, 0.7), 0 0 20px -6px ${c.glow}`
          }}
        >
          {/* Subtle top neon border accent */}
          <div
            className="absolute top-0 left-0 right-0 h-[2px] opacity-80"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${c.hex} 50%, transparent 100%)`
            }}
          />

          {/* Top Row: Track Artwork, Title, Time, and Equalizer */}
          <div className="flex items-center justify-between gap-3 mb-2">
            {/* Left: Compact Vinyl Record Icon + Track Title & Time */}
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Spinning Vinyl Record Artwork */}
              <div
                className="relative w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-[#121218] to-[#20202c] border border-white/15 flex items-center justify-center shadow-md shrink-0 overflow-hidden"
                style={{
                  boxShadow: `0 0 12px -3px ${c.glow}`
                }}
              >
                <div
                  className={`w-full h-full rounded-full flex items-center justify-center ${
                    !isMuted ? 'animate-spin' : ''
                  }`}
                  style={{ animationDuration: '4s' }}
                >
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border border-dashed border-white/20 flex items-center justify-center">
                    <div
                      className="w-1.5 h-1.5 rounded-full transition-colors"
                      style={{
                        backgroundColor: c.hex,
                        boxShadow: `0 0 6px ${c.glow}`
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Title & Time in a sleek single line */}
              <div className="min-w-0 flex items-center gap-2">
                <h4 className="text-xs sm:text-[13px] font-bold text-white truncate tracking-tight">
                  {currentTrack || (isEn ? 'StormBlack Theme' : 'Тема StormBlack')}
                </h4>
                <span className="text-[10px] sm:text-[11px] font-mono text-gray-400 shrink-0">
                  {formatTime(currentTime)}
                </span>
              </div>
            </div>

            {/* Right: Live Audio Equalizer Bars */}
            <div className="flex items-center shrink-0">
              <div className="flex items-end gap-1 h-5 px-2 py-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
                {barHeights.map((h, i) => (
                  <span
                    key={i}
                    className="w-0.5 sm:w-1 rounded-full transition-all duration-75"
                    style={{
                      height: `${Math.max(3, Math.round(h * 0.7))}px`,
                      backgroundColor: c.hex,
                      opacity: isMuted ? 0.3 : 0.85
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Row: Volume Slider & Mute Button */}
          <div className="flex items-center gap-2.5 pt-2 border-t border-white/[0.06]">
            <button
              onClick={toggleMute}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 border shrink-0 active:scale-95 cursor-pointer ${
                isMuted
                  ? 'bg-red-500/15 text-red-400 border-red-500/30'
                  : 'bg-white/[0.06] text-white hover:bg-white/[0.12] border-white/10'
              }`}
              title={isMuted ? (isEn ? 'Unmute' : 'Включить звук') : (isEn ? 'Mute' : 'Выключить звук')}
            >
              {isMuted ? (
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
              )}
            </button>

            {/* Range Slider */}
            <div className="flex-1 flex items-center gap-2.5">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="cyber-volume-slider flex-1"
                style={{
                  '--fill': `${isMuted ? 0 : fillPercent}%`,
                  '--slider-color': c.hex
                }}
              />

              <span className="text-[10px] sm:text-[11px] font-mono text-gray-400 w-8 text-right shrink-0">
                {isMuted ? '0%' : `${fillPercent}%`}
              </span>
            </div>
          </div>
        </div>

        <style>{`
          .cyber-volume-slider {
            -webkit-appearance: none;
            appearance: none;
            height: 5px;
            border-radius: 999px;
            outline: none;
            cursor: pointer;
            background: linear-gradient(
              to right,
              var(--slider-color) 0%,
              var(--slider-color) var(--fill),
              rgba(255, 255, 255, 0.08) var(--fill),
              rgba(255, 255, 255, 0.08) 100%
            );
          }
          .cyber-volume-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #ffffff;
            border: 2px solid var(--slider-color);
            box-shadow: 0 0 6px rgba(0, 0, 0, 0.5), 0 0 5px var(--slider-color);
            cursor: grab;
            transition: transform 0.1s;
          }
          .cyber-volume-slider::-webkit-slider-thumb:hover {
            transform: scale(1.2);
          }
          .cyber-volume-slider::-webkit-slider-thumb:active {
            cursor: grabbing;
            transform: scale(0.95);
          }
          .cyber-volume-slider::-moz-range-thumb {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #ffffff;
            border: 2px solid var(--slider-color);
            box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
            cursor: grab;
          }
        `}</style>
      </motion.div>
    </AnimatePresence>
  );
};

export default MusicControlPanel;